// Reusable CRUD list page for sub-resources (witnesses, evidence, etc.)
import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { api, formatError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common/Bits";
import { Plus, X, Save, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CrudPage({
  slug, title, subtitle, fields, emptyIcon, listColumns, badges = {},
  defaultItem = {}, extra,
}) {
  const { t, currentCase } = useApp();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null or item object
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!currentCase) return;
    api.get(`/cases/${currentCase.id}/${slug}`).then(r => setItems(r.data)).catch(()=>{});
  }, [currentCase, slug]);

  const save = async () => {
    try {
      if (editing.id) {
        const { data } = await api.patch(`/cases/${currentCase.id}/${slug}/${editing.id}`, editing);
        setItems(arr => arr.map(x => x.id === data.id ? data : x));
      } else {
        const { data } = await api.post(`/cases/${currentCase.id}/${slug}`, editing);
        setItems(arr => [data, ...arr]);
      }
      setEditing(null);
      toast.success(t("saved"));
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Διαγραφή;")) return;
    await api.delete(`/cases/${currentCase.id}/${slug}/${id}`);
    setItems(arr => arr.filter(x => x.id !== id));
    toast.success(t("deleted"));
  };

  const filtered = items.filter(it => {
    const matchSearch = !search || JSON.stringify(it).toLowerCase().includes(search.toLowerCase());
    const matchFilter = !filter || Object.values(badges).some(b => it[b.field] === filter);
    return matchSearch && matchFilter;
  });

  const filterValues = Array.from(new Set(items.flatMap(it => Object.values(badges).map(b => it[b.field])).filter(Boolean)));

  return (
    <div data-testid={`${slug}-page`}>
      <PageHeader
        title={title} subtitle={subtitle}
        action={
          <button data-testid={`${slug}-add-btn`} onClick={() => setEditing({ ...defaultItem })}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px]">
            <Plus className="size-4"/>{t("add")}
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          data-testid={`${slug}-search`}
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("search")}
          className="flex-1 min-w-[200px] bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
        />
        {filterValues.length > 0 && (
          <select
            data-testid={`${slug}-filter`}
            value={filter} onChange={e => setFilter(e.target.value)}
            className="bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
          >
            <option value="">{t("filter")}</option>
            {filterValues.map(v => <option key={v} value={v}>{t(v) === v ? v : t(v)}</option>)}
          </select>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={t("empty_state")} subtitle="Προσθέστε το πρώτο στοιχείο για να ξεκινήσετε." icon={emptyIcon}/>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(it => (
            <div key={it.id} data-testid={`${slug}-item-${it.id}`}
              className="surface-card surface-card-hover p-5 group cursor-pointer" onClick={() => setEditing(it)}>
              <div className="flex items-start justify-between mb-2 gap-3">
                <div className="min-w-0 flex-1">
                  <h4 className="font-heading font-semibold text-[14px] truncate" data-testid={`${slug}-item-title-${it.id}`}>{listColumns.title(it)}</h4>
                  {listColumns.subtitle && <div className="text-[12px] text-muted-foreground truncate mt-0.5">{listColumns.subtitle(it)}</div>}
                </div>
                <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`${slug}-edit-${it.id}`} onClick={e => { e.stopPropagation(); setEditing(it); }} className="p-1.5 hover:bg-muted rounded"><Edit2 className="size-3.5"/></button>
                  <button data-testid={`${slug}-delete-${it.id}`} onClick={e => { e.stopPropagation(); remove(it.id); }} className="p-1.5 hover:bg-red-500/10 hover:text-red-400 rounded"><Trash2 className="size-3.5"/></button>
                </div>
              </div>
              {listColumns.body && <p className="text-[12.5px] text-muted-foreground leading-relaxed line-clamp-3">{listColumns.body(it)}</p>}
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                {Object.entries(badges).map(([k, b]) => {
                  const v = it[b.field];
                  if (!v) return null;
                  return <StatusBadge key={k} tone={b.toneFromValue ? b.toneFromValue(v) : v}>{b.labelMap?.[v] || v}</StatusBadge>;
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {extra && <div className="mt-6">{typeof extra === "function" ? extra(items) : extra}</div>}

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid={`${slug}-edit-modal`}>
          <div className="surface-card w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="flex items-center justify-between p-5 border-b border-border/60 sticky top-0 bg-card z-10">
              <h3 className="font-heading font-semibold text-[15px]">{editing.id ? t("edit") : t("add")}</h3>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-muted rounded" data-testid={`${slug}-modal-close`}><X className="size-4"/></button>
            </div>
            <div className="p-5 space-y-4">
              {fields.map(f => (
                <div key={f.k}>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{f.label}</label>
                  {f.type === "select" ? (
                    <select
                      data-testid={`${slug}-field-${f.k}`}
                      value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })}
                      className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
                    >
                      <option value="">—</option>
                      {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <textarea
                      data-testid={`${slug}-field-${f.k}`}
                      value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })}
                      rows={f.rows || 3} placeholder={f.ph}
                      className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)] resize-none"
                    />
                  ) : (
                    <input
                      data-testid={`${slug}-field-${f.k}`}
                      type={f.type || "text"}
                      value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })}
                      placeholder={f.ph}
                      className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-border/60 sticky bottom-0 bg-card">
              <button data-testid={`${slug}-modal-cancel`} onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-border/80 text-[13px] hover:bg-muted">{t("cancel")}</button>
              <button data-testid={`${slug}-modal-save`} onClick={save} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px]"><Save className="size-4"/>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
